import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { track } from '@amplitude/analytics-browser'
import {
  aICreatorUploadAttachmentAction,
  aICreatorCheckAttachmentStatusAction,
} from 'src/reducers/ai-creator/action'
import { snackbarShowAction } from 'src/reducers/snackbar/action'
import SnackbarMessage from 'src/model/snackbar'
import { SnackbarType } from 'src/model/snackbar/type'
import { guid } from 'src/utils/guid'

export const useAttachmentManager = () => {
  const dispatch = useDispatch()
  const [attachmentLoading, setAttachmentLoading] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const attachments = useSelector(state => state.aICreator.attachments)

  const uploadFile = file => {
    track('upload_attachment', {
      type: 'pdf',
      file_name: file?.name || '',
    })

    const allowedExtensions = ['pdf']
    const fileExtension = file.name.split('.').pop().toLowerCase()

    if (!allowedExtensions.includes(fileExtension)) {
      dispatch(
        snackbarShowAction(
          new SnackbarMessage(guid().uid(), SnackbarType.ERROR, [
            'Invalid file type. Please select a PDF file.',
          ]),
        ),
      )
      return
    }

    setAttachmentLoading(true)
    const formData = new FormData()
    formData.append('file', file)

    dispatch(
      aICreatorUploadAttachmentAction(
        formData,
        file.name,
        (res, error) => {
          // Hem başarı hem hata durumunda loading'i false yap
          setAttachmentLoading(false)
          if (error) {
            dispatch(
              snackbarShowAction(
                new SnackbarMessage(guid().uid(), SnackbarType.ERROR, [
                  'File upload failed. Please try again.',
                ]),
              ),
            )
          }
        },
      ),
    )
  }

  const handleOnDropFile = e => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      uploadFile(file)
    }
  }

  const handleFileSelect = event => {
    const selectedFile = event.target.files[0]
    if (selectedFile) {
      uploadFile(selectedFile)
    }
  }

  const checkAttachmentStatus = async () => {
    const attachmentsToCheck = attachments.filter(
      attachment =>
        attachment.succeeded === false &&
        attachment.upload_status !== 'partial_failure' &&
        attachment.upload_status !== 'failed',
    )

    if (!isChecking && attachmentsToCheck.length > 0) {
      setIsChecking(true)
      for (const attachment of attachmentsToCheck) {
        dispatch(
          aICreatorCheckAttachmentStatusAction(
            attachment.attachment_id,
            response => {
              if (response.succeeded) {
                // Attachment processed successfully
              } else if (
                response.error_details &&
                response.error_details.length > 0
              ) {
                dispatch(
                  snackbarShowAction(
                    new SnackbarMessage(guid().uid(), SnackbarType.ERROR, [
                      response.error_details.join(', '),
                    ]),
                  ),
                )
              }
            },
          ),
        )
      }
      setIsChecking(false)
    }
  }

  useEffect(() => {
    const interval = setInterval(checkAttachmentStatus, 3000)
    return () => clearInterval(interval)
  }, [attachments, isChecking]) // Added isChecking to dependencies

  return {
    attachments,
    attachmentLoading,
    handleOnDropFile,
    handleFileSelect,
  }
}
